import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useOrganizations, Organization } from '@/hooks/useOrganizations';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import type { AccountType, PartnerTrack, PlanId } from '@/lib/plans';
import { getAccountTypeForPlan, getPlan, normalizePlanId } from '@/lib/plans';

interface OrganizationContextType {
  currentOrganization: Organization | null;
  setCurrentOrganization: (org: Organization | null) => void;
  organizations: Organization[];
  isLoading: boolean;
  // New: AccountType + Plan architecture
  accountType: AccountType;
  planId: PlanId;
  subAccountLimit: number;
  subAccountCount: number;
  canCreateSubAccount: boolean;
  partnerTracks: PartnerTrack[];
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

const STORAGE_KEY = 'selected_organization_id';

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { data: organizations = [], isLoading } = useOrganizations();
  const [currentOrganization, setCurrentOrganizationState] = useState<Organization | null>(null);

  // Fetch partner tracks for current organization
  const { data: partnerTracksData = [] } = useQuery({
    queryKey: ['partner-tracks', currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization?.id) return [];
      const { data, error } = await supabase
        .from('partner_tracks')
        .select('track')
        .eq('org_id', currentOrganization.id);
      if (error) return [];
      return data.map(t => t.track as PartnerTrack);
    },
    enabled: !!currentOrganization?.id,
  });

  // Load saved organization on mount
  useEffect(() => {
    if (!user || isLoading || organizations.length === 0) {
      setCurrentOrganizationState(null);
      return;
    }

    const savedOrgId = localStorage.getItem(STORAGE_KEY);
    
    if (savedOrgId) {
      const savedOrg = organizations.find(org => org.id === savedOrgId);
      if (savedOrg) {
        setCurrentOrganizationState(savedOrg);
        return;
      }
    }

    // Default to first organization
    setCurrentOrganizationState(organizations[0]);
    localStorage.setItem(STORAGE_KEY, organizations[0].id);
  }, [user, organizations, isLoading]);

  const setCurrentOrganization = (org: Organization | null) => {
    setCurrentOrganizationState(org);
    if (org) {
      localStorage.setItem(STORAGE_KEY, org.id);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  // Derive plan values from current organization
  const planId = normalizePlanId(currentOrganization?.plan_id ?? 'freemium');
  const plan = getPlan(planId);
  const accountType = currentOrganization?.account_type ?? getAccountTypeForPlan(planId);
  const subAccountLimit = plan?.subAccountLimit ?? 5;
  const subAccountCount = currentOrganization?.sub_account_count ?? 0;
  const canCreateSubAccount = subAccountLimit === -1 || subAccountCount < subAccountLimit;

  return (
    <OrganizationContext.Provider
      value={{
        currentOrganization,
        setCurrentOrganization,
        organizations,
        isLoading,
        // New: AccountType + Plan architecture
        accountType,
        planId,
        subAccountLimit,
        subAccountCount,
        canCreateSubAccount,
        partnerTracks: partnerTracksData,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganizationContext() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganizationContext must be used within an OrganizationProvider');
  }
  return context;
}
